import { useState } from "react";

import { BadgeCheck, Loader2, Monitor } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useHostMutations } from "@/features/servers/hooks/useCenterInfra";
import {
  HOST_KIND_LABELS,
  OS_SUPPORT_STATUS_LABELS,
  type IHost,
  type IOsSupportPolicy,
  type OsSupportStatus,
} from "@/features/servers/schemas/servers";
import {
  buildHostVerificationPayload,
  getVerifierLabel,
} from "@/features/servers/utils/host-verification";
import {
  evaluateOsSupport,
  formatOsLabel,
  resolveHostOsVersion,
} from "@/features/servers/utils/os-support";
import { useCurrentUser } from "@/features/users/hooks/useCurrentUser";
import { cn } from "@/shared/lib/utils";

interface FichaHostCardProps {
  institutionUid: string;
  host: IHost;
  osPolicy?: IOsSupportPolicy;
}

function supportBadgeVariant(
  status: OsSupportStatus
): "default" | "secondary" | "outline" | "destructive" {
  if (status === "recommended") return "default";
  if (status === "supported") return "outline";
  if (status === "unsupported") return "destructive";
  return "secondary";
}

function accentClass(status: OsSupportStatus, verified: boolean): string {
  if (!verified) return "border-l-amber-500";
  if (status === "unsupported") return "border-l-destructive";
  if (status === "recommended") return "border-l-emerald-500";
  if (status === "supported") return "border-l-primary";
  return "border-l-border";
}

/**
 * Tarjeta de host en ficha de consulta con verificación rápida.
 */
export function FichaHostCard({
  institutionUid,
  host,
  osPolicy,
}: FichaHostCardProps) {
  const { data: currentUser } = useCurrentUser();
  const { update } = useHostMutations(institutionUid);
  const [verifying, setVerifying] = useState(false);

  const osVersion = resolveHostOsVersion(host);
  const osLabel =
    formatOsLabel(host.osFamily, osVersion ?? undefined, host.os) || host.os;
  const osStatus = osPolicy
    ? evaluateOsSupport(osVersion, osPolicy)
    : ("unknown" as OsSupportStatus);

  const handleVerify = async () => {
    const by = getVerifierLabel(currentUser);
    if (!by) {
      toast.error("No se pudo identificar al verificador");
      return;
    }
    setVerifying(true);
    try {
      await update.mutateAsync({
        hostId: host.id,
        data: buildHostVerificationPayload(by),
      });
      toast.success(`"${host.name}" verificado`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Error al verificar host"
      );
    } finally {
      setVerifying(false);
    }
  };

  return (
    <article
      className={cn(
        "rounded-lg border border-l-4 bg-card p-3 space-y-2.5 transition-colors hover:bg-muted/20",
        accentClass(osStatus, !!host.verifiedAt)
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 space-y-1.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <Monitor className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
            <h3 className="font-medium text-sm truncate">{host.name}</h3>
          </div>
          <div className="flex flex-wrap gap-1">
            <Badge variant="secondary">{HOST_KIND_LABELS[host.kind]}</Badge>
            {osPolicy && (
              <Badge variant={supportBadgeVariant(osStatus)}>
                {OS_SUPPORT_STATUS_LABELS[osStatus]}
              </Badge>
            )}
          </div>
        </div>
        <Button
          size="sm"
          variant={host.verifiedAt ? "outline" : "default"}
          className="shrink-0 cursor-pointer print:hidden h-8"
          onClick={() => void handleVerify()}
          disabled={verifying || update.isPending}
          aria-label={
            host.verifiedAt
              ? `Re-verificar host ${host.name}`
              : `Verificar host ${host.name}`
          }
        >
          {verifying ? (
            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" aria-hidden />
          ) : (
            <BadgeCheck className="h-3.5 w-3.5 mr-1" aria-hidden />
          )}
          {host.verifiedAt ? "Re-verificar" : "Verificar"}
        </Button>
      </div>

      <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
        {host.ip && (
          <div>
            <dt className="text-muted-foreground">IP</dt>
            <dd className="font-mono">{host.ip}</dd>
          </div>
        )}
        {host.hostname && (
          <div>
            <dt className="text-muted-foreground">Hostname</dt>
            <dd className="truncate">{host.hostname}</dd>
          </div>
        )}
        {osLabel && (
          <div className="col-span-2">
            <dt className="text-muted-foreground">SO</dt>
            <dd>
              {osLabel}
              {osPolicy && osVersion && (
                <span className="text-muted-foreground">
                  {" "}
                  · rec. {osPolicy.recommended} · mín. {osPolicy.minSupported}
                </span>
              )}
            </dd>
          </div>
        )}
        {host.hardware && (
          <div className="col-span-2">
            <dt className="text-muted-foreground">Hardware</dt>
            <dd>{host.hardware}</dd>
          </div>
        )}
        {host.anydeskId && (
          <div className="col-span-2">
            <dt className="text-muted-foreground">AnyDesk</dt>
            <dd className="font-mono">
              {host.anydeskId}
              {host.anydeskPass && (
                <span className="text-muted-foreground"> / {host.anydeskPass}</span>
              )}
            </dd>
          </div>
        )}
        {(host.osUser || host.osPass) && (
          <div className="col-span-2">
            <dt className="text-muted-foreground">Usuario SO</dt>
            <dd className="font-mono">
              {[host.osUser, host.osPass].filter(Boolean).join(" / ")}
            </dd>
          </div>
        )}
      </dl>

      {host.verifiedAt ? (
        <p className="text-[11px] text-emerald-700 dark:text-emerald-400">
          Verificado: {host.verifiedAt}
          {host.verifiedBy ? ` · ${host.verifiedBy}` : ""}
        </p>
      ) : (
        <p className="text-[11px] font-medium text-amber-700 dark:text-amber-400">
          Sin verificar
        </p>
      )}

      {host.customFields.map((f, i) => (
        <p key={i} className="text-xs">
          <span className="font-medium">{f.key}:</span> {f.value}
        </p>
      ))}
    </article>
  );
}
